import type { Dispatch, SetStateAction } from 'react'
import { MdMoreHoriz } from 'react-icons/md'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { range } from '@/utils/number'

import { PaginationButton } from './PaginationButton'

type Props = {
  totalCount: number
  countPerPage: number
  currentPage: number
  transitionPage: Dispatch<SetStateAction<number>>
}

const MAX_DISPLAY_COUNT = 5

const createPageList = (
  currentPage: number,
  totalPageCount: number
): readonly number[] => {
  // 5ページ未満
  if (totalPageCount < MAX_DISPLAY_COUNT) {
    return range(1, totalPageCount)
  }

  // 最初の2ページ
  if (currentPage < 3) {
    const end = Math.min(MAX_DISPLAY_COUNT, totalPageCount)

    return range(1, end)
  }

  // 最後の2ページ
  if (currentPage > totalPageCount - 3) {
    return range(totalPageCount - (MAX_DISPLAY_COUNT - 1), totalPageCount)
  }

  return range(currentPage - 2, currentPage + 2)
}

export const Pagination = ({
  totalCount,
  countPerPage,
  currentPage,
  transitionPage,
}: Props) => {
  if (totalCount === 0) {
    return null
  }

  const lastPage = Math.ceil(totalCount / countPerPage)
  const pageList = createPageList(currentPage, lastPage)

  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === lastPage
  const isIncludeFirstPage = pageList[0] === 1
  const isIncludeLastPage = pageList.at(-1) === lastPage

  return (
    <nav aria-label="ページネーション">
      <List>
        {!isFirstPage && (
          <PaginationButton
            page="before"
            onClick={() => transitionPage((prev) => prev - 1)}
          />
        )}
        {!isIncludeFirstPage && (
          <>
            <PaginationButton page={1} onClick={() => transitionPage(1)} />
            <Ellipsis>
              <EllipsisIcon />
            </Ellipsis>
          </>
        )}
        {pageList.map((page) => (
          <PaginationButton
            page={page}
            isActive={page === currentPage}
            key={page}
            onClick={() => transitionPage(page)}
          />
        ))}
        {!isIncludeLastPage && (
          <>
            <Ellipsis>
              <EllipsisIcon />
            </Ellipsis>
            <PaginationButton
              page={lastPage}
              onClick={() => transitionPage(lastPage)}
            />
          </>
        )}
        {!isLastPage && (
          <PaginationButton
            page="next"
            onClick={() => transitionPage((prev) => prev + 1)}
          />
        )}
      </List>
    </nav>
  )
}

const List = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;

  @media screen and (max-width: 1047px) {
    gap: 8px;
  }
`

const Ellipsis = styled.div`
  display: grid;
  place-content: center;
  margin: 0 10px;

  @media screen and (max-width: 1047px) {
    margin: 0 5px;
  }
`

const EllipsisIcon = styled(MdMoreHoriz)`
  color: ${theme.colors.trialBlue};
`
